import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Chip, Grid, InputAdornment, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PostAddIcon from '@mui/icons-material/PostAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { ConfirmationDialog, DataGrid, IconBtnBase, ResourcesList } from 'Components';
import { CERTIFICATEACTIVENAMES, CERTIFICATENAMELIST, CERTIFICATENAMELISTS } from 'Constants/QueriesKeys';
import {
    CERTIFICATE_CATEGORIES,
    CERTIFICATE_NAME_SORT_FIELDS,
    CERTIFICATE_PER_PAGE,
    CERTIFICATE_PER_PAGE_OPTIONS,
    LIST_CREATE_EMPTY,
    LIST_CREATE_EXCEL,
    LIST_CREATE_MANUAL,
    NO_CERTIFICATE_CATEGORY,
    SEARCH_DEBOUNCE_MS,
} from 'Constants/CertificateNames';
import {
    deleteCertificateName,
    deleteCertificateNameList,
    fetchActiveCertificateNames,
    fetchCertificateNameLists,
    fetchCertificateNames,
    setCertificateNameListActive,
} from 'Services/CertificateNamesSvc';
import CertificateNameFormModal from './CertificateNameFormModal';
import CertificateListFormModal from './CertificateListFormModal';
import CertificateListImportModal from './CertificateListImportModal';
import CertificateListsPanel from './CertificateListsPanel';
import ActiveListsModal from './ActiveListsModal';
import { apiErrorMessage, formatCertificateCategory, formatDate } from './CertificateHelpers';

const styles = {
    tableSize: {
        height: 650,
        width: '100%',
    },
};

/**
 * The approved certificate names, organised in lists.
 *
 * Top: every list, with its size and whether it is active, and the ways to
 * create one (empty, typed names, a spreadsheet), import into one, switch lists
 * on and off, edit and delete. Only names in active lists verify or print;
 * several lists may be active at once and their names count together, each
 * name once.
 *
 * Below: the names of the selected list. Paging, searching and sorting all
 * happen on the server — a list can hold 20,000 names.
 */
const CertificateNamesPage = () => {
    const { t } = useTranslation();
    const spacing = 4;

    // ------------------------------------------------------------- lists

    const [selectedListId, setSelectedListId] = useState(null);
    const [listFormMode, setListFormMode] = useState(null);
    const [listToEdit, setListToEdit] = useState(null);
    const [listToImport, setListToImport] = useState(null);
    const [listToDelete, setListToDelete] = useState(null);
    const [isActiveModalOpen, setIsActiveModalOpen] = useState(false);
    const [togglingId, setTogglingId] = useState(null);

    const listsQuery = useQuery([CERTIFICATENAMELISTS], fetchCertificateNameLists);
    const lists = useMemo(() => listsQuery.data?.lists ?? [], [listsQuery.data]);
    const activeCount = lists.filter((list) => list.is_active).length;

    // How many distinct names verification is matching against right now —
    // only the total is wanted, so one row is fetched.
    const activeNamesQuery = useQuery([CERTIFICATEACTIVENAMES], () => fetchActiveCertificateNames({ per_page: 1 }));

    // A list just created is selected before the refetch that brings it back
    // has landed; this marks it as expected, so the fallback below waits for it
    // instead of bouncing to the default list.
    const pendingSelection = useRef(null);

    // Start on the default list, and fall back to it when the selected list is
    // deleted.
    useEffect(() => {
        if (!lists.length) {
            return;
        }

        if (lists.some((list) => list.id === selectedListId)) {
            pendingSelection.current = null;

            return;
        }

        if (selectedListId !== null && pendingSelection.current === selectedListId) {
            return;
        }

        setSelectedListId(listsQuery.data?.defaultId ?? lists[0].id);
    }, [lists, selectedListId, listsQuery.data]);

    const selectedList = lists.find((list) => list.id === selectedListId) ?? null;

    // ------------------------------------------------- names of the list

    const [certificateNameList, setCertificateNameList] = useState([]);
    const [paginator1, setPaginator1] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(CERTIFICATE_PER_PAGE);

    // Two pieces of search state on purpose: `searchInput` is what the operator
    // sees, `search` is what goes into the react-query key. The other list pages
    // put the raw input in the key, which fires a request per keystroke.
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState(NO_CERTIFICATE_CATEGORY);
    const [sortModel, setSortModel] = useState([]);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [recordToEdit, setRecordToEdit] = useState(null);
    const [recordToDelete, setRecordToDelete] = useState(null);

    const applySearch = useMemo(
        () =>
            debounce((value) => {
                setSearch(value);
                // Page 7 of the unfiltered table is page 7 of nothing once a
                // search narrows it to three rows.
                setPage(1);
            }, SEARCH_DEBOUNCE_MS),
        [],
    );

    // A debounced setState firing after this page has gone is a React warning at
    // best, so the pending call is dropped on unmount.
    useEffect(() => () => applySearch.cancel(), [applySearch]);

    // An unknown sort_by is a 422, not an ignored parameter, so only whitelisted
    // columns are ever sent. MUI clears the sort on the third header click,
    // which leaves `sort` null - that means "no sort_by at all".
    const activeSort = sortModel[0];
    const sortBy =
        activeSort?.sort && CERTIFICATE_NAME_SORT_FIELDS.includes(activeSort.field) ? activeSort.field : undefined;
    const direction = sortBy ? activeSort.sort : undefined;

    const certificateNameListRender = useQuery(
        [CERTIFICATENAMELIST, { listId: selectedListId, page, perPage, search, category, sortBy, direction }],
        () =>
            fetchCertificateNames({
                certificate_name_list_id: selectedListId,
                page,
                per_page: perPage,
                search,
                category,
                sort_by: sortBy,
                direction,
            }).then((list) => {
                setCertificateNameList(list.rows);
                setPaginator1(list.total);

                return list;
            }),
        // Never fetch without a list: that would be every list's names mixed.
        { keepPreviousData: true, enabled: Boolean(selectedListId) },
    );

    // No useMutation and no queryClient.invalidateQueries anywhere in this
    // codebase - a write refetches what it changed by hand. Counts on the lists
    // and the active-name total move with almost every write here.
    const refreshLists = () => {
        listsQuery.refetch();
        activeNamesQuery.refetch();
    };

    const refreshAll = () => {
        refreshLists();
        certificateNameListRender.refetch();
    };

    // ------------------------------------------------------ list handlers

    const handleSelectList = (id) => {
        if (id === selectedListId) {
            return;
        }

        pendingSelection.current = id;
        setSelectedListId(id);
        setPage(1);
        setSearchInput('');
        setSearch('');
        applySearch.cancel();
    };

    const handleToggleActive = async (list, active) => {
        setTogglingId(list.id);
        try {
            await setCertificateNameListActive(list.id, active);
            toast.success(
                t(active ? 'admin.certificateNames.lists.activated' : 'admin.certificateNames.lists.deactivated', {
                    name: list.name,
                }),
            );
            refreshLists();
        } catch (error) {
            toast.error(apiErrorMessage(error, t('admin.certificateNames.lists.saveFailed')));
        }
        setTogglingId(null);
    };

    const handleListSaved = (list, { keepOpen } = {}) => {
        if (!keepOpen) {
            setListFormMode(null);
            setListToEdit(null);
        }

        if (list?.id) {
            handleSelectList(list.id);
        }

        refreshAll();
    };

    const handleDeleteList = async () => {
        const list = listToDelete;
        setListToDelete(null);

        try {
            const { names_deleted: deleted } = await deleteCertificateNameList(list.id);
            toast.success(
                t('admin.certificateNames.lists.deleted', {
                    name: list.name,
                    count: Number(deleted ?? 0).toLocaleString(),
                }),
            );
            refreshLists();
        } catch (error) {
            toast.error(apiErrorMessage(error, t('admin.certificateNames.lists.deleteFailed')));
        }
    };

    // ------------------------------------------------------ name handlers

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value);
        applySearch(event.target.value);
    };

    const handleCategoryChange = (event) => {
        setCategory(event.target.value);
        setPage(1);
    };

    const handleSortModelChange = (newSortModel) => {
        setSortModel(newSortModel);
        setPage(1);
    };

    const handleAdd = () => {
        setRecordToEdit(null);
        setIsFormOpen(true);
    };

    const handleEdit = (record) => {
        setRecordToEdit(record);
        setIsFormOpen(true);
    };

    const handleSaved = () => {
        setIsFormOpen(false);
        refreshAll();
    };

    const handleDelete = async () => {
        const record = recordToDelete;
        setRecordToDelete(null);

        try {
            await deleteCertificateName(record.id);
            toast.success(t('toast.success.deletedSuccessfully'));
            refreshLists();

            if (certificateNameList.length === 1 && page > 1) {
                // That was the only row on this page. Refetching would show an
                // empty table, so step back one page instead (which refetches
                // through the query key anyway).
                setPage(page - 1);
            } else {
                certificateNameListRender.refetch();
            }
        } catch (error) {
            toast.error(apiErrorMessage(error));
        }
    };

    const columns1 = [
        {
            field: 'serial',
            headerName: t('admin.certificateNames.rowNumber'),
            width: 80,
            editable: false,
            // A position in the result set, not a value the API can sort on.
            sortable: false,
        },
        {
            field: 'name',
            headerName: t('admin.certificateNames.name'),
            minWidth: 380,
            flex: 1,
            editable: false,
        },
        {
            field: 'category',
            headerName: t('admin.certificateNames.category'),
            width: 170,
            editable: false,
            renderCell: (row) => formatCertificateCategory(t, row.row.category),
        },
        {
            field: 'created_at',
            headerName: t('admin.certificateNames.createdAt'),
            width: 130,
            editable: false,
            renderCell: (row) => formatDate(row.row.created_at),
        },
        {
            field: 'updated_at',
            headerName: t('admin.certificateNames.updatedAt'),
            width: 130,
            editable: false,
            renderCell: (row) => formatDate(row.row.updated_at),
        },
        {
            field: 'action',
            headerName: t('common.actions'),
            width: 120,
            editable: false,
            sortable: false,
            renderCell: (row) => {
                return (
                    <div className="flex items-center">
                        <IconBtnBase title={t('common.edit')} onClick={() => handleEdit(row?.row)} size="large">
                            <BorderColorOutlinedIcon />
                        </IconBtnBase>
                        <IconBtnBase title={t('common.delete')} onClick={() => setRecordToDelete(row?.row)} size="large">
                            <DeleteOutlineOutlinedIcon />
                        </IconBtnBase>
                    </div>
                );
            },
        },
    ];

    const activeTotal = activeNamesQuery.data?.total;

    return (
        <>
            <ResourcesList
                headerProps={{
                    title: t('admin.certificateNames.title'),
                    EndSide: (
                        <div className="flex">
                            <IconBtnBase title={t('admin.certificateNames.lists.refresh')} onClick={refreshAll} size="large">
                                <RefreshIcon />
                            </IconBtnBase>
                        </div>
                    ),
                }}
                Section={
                    <div className="mt-8 space-y-6">
                        {/* ------------------------------------------ lists */}
                        <Box className="space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <Typography variant="h6">{t('admin.certificateNames.lists.title')}</Typography>
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        variant="outlined"
                                        startIcon={<PlaylistAddIcon />}
                                        onClick={() => setListFormMode(LIST_CREATE_EMPTY)}
                                    >
                                        {t('admin.certificateNames.lists.createEmpty')}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<PostAddIcon />}
                                        onClick={() => setListFormMode(LIST_CREATE_MANUAL)}
                                    >
                                        {t('admin.certificateNames.lists.createManual')}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<UploadFileIcon />}
                                        onClick={() => setListFormMode(LIST_CREATE_EXCEL)}
                                    >
                                        {t('admin.certificateNames.lists.createExcel')}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<ToggleOnOutlinedIcon />}
                                        onClick={() => setIsActiveModalOpen(true)}
                                        disabled={!lists.length}
                                    >
                                        {t('admin.certificateNames.lists.manageActive')}
                                    </Button>
                                </div>
                            </div>

                            {listsQuery.isSuccess &&
                                (activeCount === 0 ? (
                                    <Alert severity="warning">{t('admin.certificateNames.lists.noneActiveBanner')}</Alert>
                                ) : (
                                    <Alert severity="info">
                                        {t('admin.certificateNames.lists.activeSummary', {
                                            active: activeCount,
                                            total: lists.length,
                                            names: activeTotal === undefined ? '…' : Number(activeTotal).toLocaleString(),
                                        })}
                                    </Alert>
                                ))}

                            {listsQuery.isError && (
                                <Alert severity="error">
                                    {apiErrorMessage(listsQuery.error, t('admin.certificateNames.lists.loadFailed'))}
                                </Alert>
                            )}

                            <CertificateListsPanel
                                lists={lists}
                                selectedId={selectedListId}
                                busyId={togglingId}
                                onSelect={handleSelectList}
                                onToggleActive={handleToggleActive}
                                onImport={setListToImport}
                                onEdit={setListToEdit}
                                onDelete={setListToDelete}
                            />
                        </Box>

                        {/* ---------------------------- names of the list */}
                        {selectedList && (
                            <Box>
                                <div className="flex flex-wrap items-center justify-between gap-3 pb-4">
                                    <div className="flex items-center gap-2">
                                        <Typography variant="h6">
                                            {t('admin.certificateNames.lists.namesIn', { name: selectedList.name })}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            color={selectedList.is_active ? 'success' : 'default'}
                                            label={
                                                selectedList.is_active
                                                    ? t('admin.certificateNames.lists.active')
                                                    : t('admin.certificateNames.lists.inactive')
                                            }
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outlined"
                                            startIcon={<UploadFileIcon />}
                                            onClick={() => setListToImport(selectedList)}
                                        >
                                            {t('admin.certificateNames.lists.importInto')}
                                        </Button>
                                        <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={handleAdd}>
                                            {t('admin.certificateNames.addTitle')}
                                        </Button>
                                    </div>
                                </div>

                                {!selectedList.is_active && (
                                    <Alert severity="warning" className="mb-4">
                                        {t('admin.certificateNames.lists.inactiveListBanner')}
                                    </Alert>
                                )}

                                <Grid container spacing={spacing}>
                                    <Grid item xs={12} md={8}>
                                        <h6 className="labelForm pb-2">{t('admin.certificateNames.search')}</h6>
                                        <TextField
                                            fullWidth
                                            id="certificate-names-search"
                                            placeholder={t('admin.certificateNames.searchPlaceholder')}
                                            onChange={handleSearchChange}
                                            value={searchInput}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <h6 className="labelForm pb-2">{t('admin.certificateNames.category')}</h6>
                                        <TextField
                                            select
                                            fullWidth
                                            id="certificate-names-category"
                                            value={category}
                                            onChange={handleCategoryChange}
                                            // Without displayEmpty MUI renders a blank
                                            // box for the empty value instead of the
                                            // "All categories" option label.
                                            SelectProps={{ displayEmpty: true }}
                                        >
                                            <MenuItem value={NO_CERTIFICATE_CATEGORY}>
                                                {t('admin.certificateNames.allCategories')}
                                            </MenuItem>
                                            {CERTIFICATE_CATEGORIES.map((value) => (
                                                <MenuItem key={value} value={value}>
                                                    {t(`admin.certificateNames.categories.${value}`)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <Box className="mt-8" sx={styles.tableSize}>
                                    <Grid container spacing={spacing}>
                                        <Grid item xs={12}>
                                            <Paper>
                                                <DataGrid
                                                    rowCount={paginator1}
                                                    pagination
                                                    paginationMode="server"
                                                    sortingMode="server"
                                                    sortModel={sortModel}
                                                    onSortModelChange={handleSortModelChange}
                                                    // MUI page indexes are zero-based
                                                    // while Laravel pages are one-based.
                                                    // Passing `page` back in keeps the
                                                    // grid from sitting on page 4 after a
                                                    // filter reset it to 1.
                                                    page={page - 1}
                                                    onPageChange={(newPage) => setPage(newPage + 1)}
                                                    pageSize={perPage}
                                                    rowsPerPageOptions={CERTIFICATE_PER_PAGE_OPTIONS}
                                                    onPageSizeChange={(newPageSize) => {
                                                        setPerPage(newPageSize);
                                                        setPage(1);
                                                    }}
                                                    loading={certificateNameListRender?.isLoading}
                                                    rows={certificateNameList}
                                                    columns={columns1}
                                                />
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        )}
                    </div>
                }
            />

            <CertificateNameFormModal
                open={isFormOpen}
                record={recordToEdit}
                listId={selectedListId}
                onClose={() => setIsFormOpen(false)}
                onSaved={handleSaved}
            />

            <CertificateListFormModal
                open={Boolean(listFormMode) || Boolean(listToEdit)}
                mode={listFormMode}
                record={listToEdit}
                onClose={() => {
                    setListFormMode(null);
                    setListToEdit(null);
                }}
                onSaved={handleListSaved}
            />

            <CertificateListImportModal
                open={Boolean(listToImport)}
                list={listToImport}
                onClose={() => setListToImport(null)}
                onImported={(list) => {
                    if (list?.id) {
                        handleSelectList(list.id);
                    }
                    refreshAll();
                }}
            />

            <ActiveListsModal
                open={isActiveModalOpen}
                lists={lists}
                onClose={() => setIsActiveModalOpen(false)}
                onSaved={() => {
                    setIsActiveModalOpen(false);
                    refreshLists();
                }}
            />

            <ConfirmationDialog
                isOpen={Boolean(listToDelete)}
                title={t('admin.certificateNames.lists.deleteTitle')}
                text={t('admin.certificateNames.lists.deleteText', {
                    name: listToDelete?.name ?? '',
                    count: Number(listToDelete?.names_count ?? 0).toLocaleString(),
                })}
                onReject={() => setListToDelete(null)}
                onResolve={handleDeleteList}
            />

            <ConfirmationDialog
                isOpen={Boolean(recordToDelete)}
                title={t('admin.certificateNames.deleteTitle')}
                text={t('admin.certificateNames.deleteText', { name: recordToDelete?.name ?? '' })}
                onReject={() => setRecordToDelete(null)}
                onResolve={handleDelete}
            />
        </>
    );
};

export default CertificateNamesPage;
